import { humanize } from 'common/helpers/humanize';
import { getLocationTypeLabelForName } from 'common/utils/locationTypeKey';
import i18n from '../../../i18n/config';
import { formatComponentSubtype } from '../MachineDetails/MachineFmea/machineFmea.constants';

/** @param {string} s */
const norm = s => String(s || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_|_$/g, '');

const CRITICALITY_SLUG = {
  'Very High': 'veryHigh',
  High: 'high',
  Medium: 'medium',
  Low: 'low',
  'Very Low': 'veryLow'
};

const CONDITION_SLUG = {
  Good: 'good',
  Slight: 'slight',
  Moderate: 'moderate',
  Warning: 'warning',
  Critical: 'critical',
  Unknown: 'unknown'
};

/** Map health score band label to i18n key segment (5- and 3-level bands). */
const healthScoreTextToKey = (text) => {
  if (text === 'unknown') return 'unknown';
  return String(text).replace(/<\s*/g, 'lt').replace(/\s/g, '');
};

const TRANSMISSION_SLUG_ALIASES = {
  flex_coupling: 'flexible_coupling',
};

function pickFirstTranslation(t, keys) {
  const hit = keys.find(key => i18n.exists(key));
  return hit ? t(hit) : null;
}

function getTransmissionSlug(raw) {
  const slug = norm(raw);
  return TRANSMISSION_SLUG_ALIASES[slug] || slug;
}

function getTransmissionLabelKeys(slug) {
  return [
    `assets.options.transmission.${slug}`,
    `diagnosticRules.form.componentSubtypeOptions.${slug}`,
    `machineBuilder.componentTypes.${slug}`,
    `assets.options.componentTypes.${slug}`,
  ];
}

/**
 * Localized label for a component subtype slug (e.g. `centrifugal_pump`).
 * `diagnosticRules.form.componentSubtypeOptions` is the most complete set (75 entries,
 * translated in all locales); `assets.options.componentTypes` is a narrower fallback.
 */
export function getComponentSubtypeDisplayLabel(t, raw) {
  if (raw === undefined || raw === null || raw === '') return '';
  const slug = getTransmissionSlug(raw);
  const translated = pickFirstTranslation(t, [
    `diagnosticRules.form.componentSubtypeOptions.${slug}`,
    `assets.options.componentTypes.${slug}`,
  ]);
  // formatComponentSubtype (not humanize) special-cases AC/DC/SAG/PAC, so the
  // fallback reads "AC Induction Motor" rather than "Ac Induction Motor".
  return translated || formatComponentSubtype(raw);
}

/** Localized transmission / drive component label (filter options and table cells). */
export function getTransmissionDisplayLabel(t, raw) {
  if (raw === undefined || raw === null || raw === '') return '';
  const slug = getTransmissionSlug(raw);
  const translated = pickFirstTranslation(t, getTransmissionLabelKeys(slug));
  return translated || humanize(raw);
}

/**
 * User-visible label for Assets list filter options (MachinesPage filters).
 * @param {import('i18next').TFunction} t
 * @param {string} filterName — searchParams key (state, vfd, …)
 * @param {*} option — primitive or { text, value, path, name, groupName }
 * @param {string} [labelField] — object field to treat as id for i18n ('text'|'path'|'name'|'').
 */
export function getAssetListFilterOptionLabel(t, filterName, option, labelField = '') {
  const isObj = option !== null && typeof option === 'object';
  const rawPrimitive = !isObj ? option : undefined;
  const field = labelField || 'text';

  const pickText = () => {
    if (!isObj) return rawPrimitive;
    if (field === 'path' && option.path != null) return option.path;
    if (field === 'name' && option.name != null) return option.name;
    if (option[field] != null) return option[field];
    if (option.text != null) return option.text;
    if (option.value != null && typeof option.value !== 'object') return option.value;
    return '';
  };

  const raw = pickText();
  if (raw === undefined || raw === null || raw === '') return '';

  switch (filterName) {
    case 'state':
      return t(`assets.options.state.${raw}`, { defaultValue: String(raw) });
    case 'orientation':
      return t(`assets.options.orientation.${raw}`, { defaultValue: humanize(raw) });
    case 'component_subtype':
      return t(`assets.options.componentTypes.${raw}`, { defaultValue: humanize(String(raw).replace(/_/g, ' ')) });
    case 'criticality': {
      const slug = CRITICALITY_SLUG[raw] || norm(raw);
      return t(`assets.options.criticality.${slug}`, { defaultValue: String(raw) });
    }
    case 'condition': {
      const slug = CONDITION_SLUG[raw] || norm(raw);
      return t(`assets.options.condition.${slug}`, { defaultValue: String(raw) });
    }
    case 'transmission':
      return getTransmissionDisplayLabel(t, raw);
    case 'location':
      return getLocationTypeLabelForName(t, String(raw));
    case 'area':
      // Area rows are user-defined hierarchy paths; show as-is.
      return String(raw);
    case 'vfd':
      return t(`assets.options.analytics.vfd.${raw}`, { defaultValue: humanize(String(raw).replace(/_/g, ' ')) });
    case 'events': {
      const id = isObj && option.value != null ? norm(option.value) : norm(raw);
      return t(`assets.options.analytics.events.${id}`, { defaultValue: String(raw) });
    }
    case 'health_score': {
      const textKey = healthScoreTextToKey(String(raw));
      return t(`assets.options.analytics.healthScoreBands.${textKey}`, { defaultValue: String(raw) });
    }
    case 'asset_setup':
      return t(`assets.options.analytics.assetSetup.${raw}`, { defaultValue: humanize(String(raw).replace(/_/g, ' ')) });
    case 'alarm_metrics':
      return t(`assets.options.alarmMetrics.${raw}`, { defaultValue: humanize(String(raw).replace(/_/g, ' ')) });
    case 'attention_criteria':
      return t(`assets.options.attentionCriteria.${raw}`, { defaultValue: humanize(raw) });
    case 'task_type':
      return t(`assets.options.taskTypes.${raw}`, { defaultValue: humanize(String(raw).replace(/_/g, ' ')) });
    default:
      return humanize(raw);
  }
}

export function getAssetListFilterGroupLabel(t, groupName) {
  const g = norm(groupName);
  return t(`assets.options.analytics.assetSetupGroups.${g}`, { defaultValue: humanize(groupName) });
}

/**
 * Values shown in list rows (ReasonItem) that use former customLabels / asset setup keys.
 */
export function translateSharedAssetFilterValue(t, value) {
  if (value == null || value === '') return '';
  const v = String(value);
  const tryKeys = [
    `assets.options.analytics.vfd.${v}`,
    `assets.options.analytics.assetSetup.${v}`,
    `assets.options.analytics.legacyCustomLabels.${v}`
  ];
  const hit = tryKeys.find(key => i18n.exists(key));
  return hit ? t(hit) : humanize(v);
}
